import {Utils} from '@busuu/legacy-core';
import RegistrationService from 'registration/registration-service.js';
import RegistrationTracking from 'tracking/pages/tracking-register.js';
import CaptchaService from 'captcha/captcha-service.js';
import ReferralService from 'referral/referral-service.js';
import FormUtils from 'helpers/form-utils.js';
import ConfigService from 'config/config-service.js';
import {validateTermsAcceptance} from 'registration/registration-busuu.js';
import {AuthenticationAppleConstant} from '../authentication/authentication-constants';

let errorElement;
let AppleButton;
let optInToggleElem;
let optInPromotions;
let userInfo = '';
const disabledClass = 'social-auth--disabled';

const appleConfigObject = {
    clientId: AuthenticationAppleConstant.clientId,
    scope: AuthenticationAppleConstant.scope,
    redirectURI: AuthenticationAppleConstant.redirectURI,
    state: AuthenticationAppleConstant.state,
    usePopup: AuthenticationAppleConstant.usePopup,
};

const getTranslation = (key) => {
    return window.BUSUU_TRANSLATIONS[key] || 'NOT FOUND';
};

/**
 * Error Tracking sent after Google API
 * fail or Busuu API fail
 * @param {Object}
 */
const sendTrackingError = (type) => {
    RegistrationTracking.sendRegistrationErrorTracking('APPLE', {
        error: type,
    });
};

/**
 * Show the error in case something went wrong
 * @return {String}
 */
const getErrorTextMessage = () => {
    return `${getTranslation('ERROR')} - ${getTranslation('TRY_AGAIN')}`;
};

const showError = (type, status) => {
    const code = status ? `[${status} - ${type}]` : `[${type}]`;
    const message = `${getErrorTextMessage()}<br><small>${code} </small>`;
    errorElement.innerHTML = message;
    FormUtils.showElement(errorElement);
    window.scrollTo(0, 0);
};

const handleError = ({type = 'UNKNOWN_TYPE', status = null}) => {
    const ignoreError = ['popup_closed_by_user'].includes(type);
    sendTrackingError(type);
    if (!ignoreError) {
        showError(type, status);
    }
};

const redirect = (url) => {
    window.location.href = url;
};

const onRegistrationSuccess = ({user, registrationResponse}) => {
    const redirectURL = RegistrationService.getAfterRegistrationURL({
        defaultURL: AppleButton.getAttribute('data-redirect'),
        registrationResponse,
        user,
    });

    if (registrationResponse.backendRedirection) {
        return redirect(redirectURL);
    }

    return RegistrationTracking.sendRegistrationSuccessTracking('APPLE', {
        email_opt_in: optInPromotions,
    }).then(redirect.bind(null, redirectURL));
};

const createAppleUser = (appleJWTToken, captchaToken) => {
    const getParams = (countryCode) => {
        const getElementValue = (el) => {
            return el.value === 'en' ? 'enc' : el.value;
        };

        const referralCode = ReferralService.getReferralUserCode();
        const perkToken = ReferralService.getPerkToken();
        const timezone = RegistrationService.getTimezoneOffset();
        const defaultInterfaceLang = Utils.getById('register-interface-lang');
        const interfaceLang = RegistrationService.getInterfaceLanguage(defaultInterfaceLang, countryCode);
        const learningLang = getElementValue(Utils.getById('register-learning-lang'));
        optInPromotions = RegistrationService.canOptInPromotions(countryCode, optInToggleElem);

        return {
            token: appleJWTToken,
            learning_language: learningLang,
            interface_language: interfaceLang,
            speaking_language: interfaceLang,
            opt_in_promotions: optInPromotions,
            ...(userInfo && {
                username: userInfo,
            }),
            ...(referralCode && {
                referral_token: referralCode,
            }),
            ...(perkToken && {
                perk_token: perkToken,
            }),
            ...(timezone && {timezone}),
            ...(captchaToken && {
                captcha_token: captchaToken,
            }),
        };
    };

    return ConfigService.getCountryCode()
        .catch(() => {
            return null;
        })
        .then(getParams)
        .then(RegistrationService.createAppleUser)
        .then(onRegistrationSuccess);
};

const initCaptcha = (appleJWTToken) => {
    const onCaptchaInit = (result) => {
        return createAppleUser(appleJWTToken, result.token);
    };

    CaptchaService.initializeAndRenderChallenge({
        endpointType: 'register_vendor',
        containerID: 'register-recaptcha',
    })
        .then(onCaptchaInit)
        .catch(handleError);
};

const initAppleAuth = () => {
    if (!window.AppleID) {
        return;
    }

    AppleButton = Utils.getById('appleid-signin-button');
    optInToggleElem = Utils.getById('register-communications-opt-in-toggle');
    errorElement = Utils.getById('error-generic');

    Utils.removeClass(AppleButton, disabledClass);

    AppleButton.addEventListener('click', async function () {
        if (!validateTermsAcceptance()) {
            return;
        }

        FormUtils.hideElement(errorElement);
        try {
            const data = await window.AppleID.auth.signIn();
            // Handle successful response.

            const {id_token: appleJWTToken, state} = data.authorization;
            if (data.user) {
                const {name} = data.user;
                userInfo = `${name?.firstName ?? ''} ${name?.lastName ?? ''}`;
            }

            if (state === AuthenticationAppleConstant.state) {
                initCaptcha(appleJWTToken);
            }
        } catch (error) {
            // Handle error.
            handleError({type: error.error});
        }
    });

    window.AppleID.auth.init(appleConfigObject);
};

export {initAppleAuth};
